<template>
  <InfusePageLayout>
    <div class="flex flex-col gap-6 overflow-y-auto">
      <CentralSystemTable
        :create-enabled="true"
        :editable="true"
        :loading="isLoading"
        :removable="true"
        :rows="centralSystems"
        :search-enabled="true"
        :search-query="searchQuery"
        :has-more-items="hasNextPage"
        @create="createModalVisible = true"
        @edit="editRow"
        @click-row="
          (centralSystem: CentralSystem) => redirectToDetails(router, centralSystem.id, 'central-systems.overview')
        "
        @remove="(centralSystem: CentralSystem) => confirmRemove(centralSystem)"
        @search="(query: string) => search(query)"
        @load="!isFetching && fetchNextPage()"
      />

      <CreateCentralSystemModal
        v-if="createModalVisible"
        :loading="isCreating"
        @create="(centralSystem: CreateCentralSystem) => create(centralSystem)"
        @close="createModalVisible = false"
      />

      <UpdateCentralSystemModal
        v-if="updateModalVisible && selectedRow"
        :central-system="selectedRow"
        :loading="isUpdating"
        @update="(centralSystem: CentralSystem) => update(centralSystem)"
        @close="updateModalVisible = false"
      />

      <InfuseConfirmModal
        v-if="removeModalVisible"
        :action-title="t('e_connector.centralSystems.modal.remove.title')"
        verification-text="delete"
        @confirm="remove"
        @close="removeModalVisible = false"
      />
    </div>
  </InfusePageLayout>
</template>

<script setup lang="ts">
import {
  CentralSystemTable,
  CreateCentralSystemModal,
  UpdateCentralSystemModal
} from '@infuse/e-connector/central-system/ui';
import { InfusePageLayout } from '@infuse/ui/layouts';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { replaceQueryParams } from '@infuse/ui/utils';
import { CentralSystem } from '@infuse/e-connector/central-system/domain';
import { redirectToDetails } from '@infuse/e-connector/shared/utils';
import { useCentralSystems } from '@infuse/e-connector/central-system/queries';
import {
  useCreateCentralSystem,
  useDeleteCentralSystem,
  useUpdateCentralSystem
} from '@infuse/e-connector/central-system/mutations';
import { CreateCentralSystem } from '@infuse/api/e-connector/central-system';
import { InfuseConfirmModal } from '@infuse/ui/components';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const { centralSystems, searchQuery, isLoading, hasNextPage, isFetching, fetchNextPage } = useCentralSystems();
const { mutateAsync: createCentralSystem, isPending: isCreating } = useCreateCentralSystem();
const { mutateAsync: updateCentralSystem, isPending: isUpdating } = useUpdateCentralSystem();
const { mutateAsync: deleteCentralSystem } = useDeleteCentralSystem();

const createModalVisible = ref<boolean>(false);
const updateModalVisible = ref<boolean>(false);
const removeModalVisible = ref<boolean>(false);
const selectedRow = ref<CentralSystem>();

watch(
  () => route.query.search,
  (search) => {
    searchQuery.value = search?.toString();
  },
  { immediate: true }
);

function search(search: string) {
  searchQuery.value = search;
  replaceQueryParams(router, 'central-systems', { search: searchQuery.value });
}

async function create(centralSystem: CreateCentralSystem) {
  await createCentralSystem(centralSystem);
  createModalVisible.value = false;
}

async function update(centralSystem: CentralSystem) {
  await updateCentralSystem(centralSystem);
  updateModalVisible.value = false;
}

function editRow(row: CentralSystem) {
  selectedRow.value = row;
  updateModalVisible.value = true;
}

function confirmRemove(row: CentralSystem) {
  selectedRow.value = row;
  removeModalVisible.value = true;
}

async function remove() {
  if (!selectedRow.value) {
    return;
  }

  await deleteCentralSystem(selectedRow.value.id);

  removeModalVisible.value = false;
  selectedRow.value = undefined;
}
</script>
